import "./TemplateCardList.scss";

import {
  Alert,
  Button,
  Flex,
  List,
  ListItemProps,
  ShorthandValue,
  Text,
  Tooltip,
} from "@fluentui/react-northstar";
import { QuestionnaireTemplateDTO } from "@teams/types/api";
import React, { useEffect, useState } from "react";

import { EmptyResultsComponent } from "../../../../_shared/components/EmptyResultsComponent";
import { TeamsLoader } from "../../../../_shared/components/TeamsLoader";
import { useTeamsContext } from "../../../../_shared/state/TeamsContextProvider";
import { useTheme } from "../../../../_shared/state/ThemeProvider";
import { useTemplates } from "../../../actions/useTemplates";
import { TemplateCard, TemplateIconComponent } from "./TemplateCard";
import { TemplateDetailsDialog } from "./TemplateDetailsDialog";
import { TemplateManagementMenu } from "./TemplateManagementMenu";

export type TemplateCardListCompactSettings = {
  hideMenu?: boolean;
  hideDetails?: boolean;
  hideMediaIcon?: boolean;
};

export type TemplateCardListProps = {
  scope: "personal" | "global";
  searchFilter: string | undefined;
  onSelect: (template: QuestionnaireTemplateDTO) => void;
  actionLabel: string;
  isCompact?: boolean;
  header?: React.ReactNode;
  compactSettings?: TemplateCardListCompactSettings;
  handleSearchResultCount?: (count: number) => void;
};

export const TemplateCardList: React.FC<TemplateCardListProps> = ({
  scope,
  searchFilter,
  onSelect,
  actionLabel,
  isCompact,
  compactSettings,
  header,
  handleSearchResultCount,
}) => {
  const [selectedTemplate, setSelectedTempate] = useState<QuestionnaireTemplateDTO>();
  const [showDetailsDialog, setShowDetailsDialog] = useState<boolean>(false);
  const { teamsContext } = useTeamsContext();

  const { loadTemplates, isTemplatesLoading, templates } = useTemplates();
  const [filteredTemplates, setFilteredTemplates] = useState<QuestionnaireTemplateDTO[]>([]);

  useEffect(() => {
    loadTemplates(teamsContext, scope);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  useEffect(() => {
    const filtered = !searchFilter
      ? templates
      : templates.filter(template => {
          return (
            template.templateName.toLocaleLowerCase().includes(searchFilter.toLocaleLowerCase()) ||
            template.templateDescription
              .toLocaleLowerCase()
              .includes(searchFilter.toLocaleLowerCase())
          );
        });

    const sorted = filtered.sort((a, b) => (a.templateName > b.templateName ? 1 : -1));

    setFilteredTemplates(sorted);
    handleSearchResultCount?.(sorted.length);
  }, [searchFilter, handleSearchResultCount, templates]);

  return (
    <Flex column gap="gap.small">
      {header}

      {isTemplatesLoading && <TeamsLoader isLoading />}

      {!isCompact &&
        !isTemplatesLoading &&
        (filteredTemplates.length > 0 ? (
          <Flex column>
            {filteredTemplates.map((template, index) => (
              <TemplateCard
                key={index}
                template={template}
                onSelect={onSelect}
                actionLabel={actionLabel}
                onDelete={() => loadTemplates(teamsContext, scope)}
              />
            ))}
          </Flex>
        ) : (
          <EmptyResultsComponent />
        ))}

      {isCompact && (
        <div className="template-card-list__authoring-container">
          {filteredTemplates.length === 0 && (
            <Alert info content={searchFilter ? "No results found" : "No templates found"} />
          )}

          <List
            truncateContent
            truncateHeader
            items={filteredTemplates.map<ShorthandValue<ListItemProps>>(t => ({
              key: t.id,
              className: "template-card-list__item",
              media: !compactSettings?.hideMediaIcon ? <TemplateMedia template={t} /> : undefined,
              truncateHeader: true,
              header: t.templateDescription ? (
                <Tooltip
                  trigger={<Text content={t.templateName} weight="semibold" />}
                  content={t.templateDescription}
                />
              ) : (
                <Text content={t.templateName} weight="semibold" />
              ),
              content: !compactSettings?.hideDetails ? (
                <TemplateComponent template={t} />
              ) : undefined,
              endMedia: (
                <Flex gap="gap.medium" vAlign="center">
                  {!compactSettings?.hideDetails && (
                    <Button
                      data-tname="show-template-details"
                      text
                      size="small"
                      content="Details"
                      onClick={() => {
                        setSelectedTempate(t);
                        setShowDetailsDialog(true);
                      }}
                    />
                  )}

                  {!compactSettings?.hideMenu && (
                    <TemplateManagementMenu
                      template={t}
                      onDeleted={() => loadTemplates(teamsContext, scope)}
                    />
                  )}

                  <Button size="small" content={actionLabel} onClick={() => onSelect(t)} text />
                </Flex>
              ),
            }))}
          />
        </div>
      )}

      {selectedTemplate && (
        <TemplateDetailsDialog
          open={showDetailsDialog}
          template={selectedTemplate}
          onCancel={() => setShowDetailsDialog(false)}
          onConfirm={() => {
            onSelect(selectedTemplate);
            setShowDetailsDialog(false);
          }}
          actionLabel={actionLabel}
        />
      )}
    </Flex>
  );
};

const TemplateComponent: React.FC<{
  template: QuestionnaireTemplateDTO;
}> = ({ template }) => {
  return (
    <Flex column>
      <Text
        content={`💬 ${template.questions.length} question${
          template.questions.length > 1 ? "s" : ""
        }`}
      />
      <Text
        className="template-card-list__item-description"
        content={template.templateDescription}
      />
    </Flex>
  );
};

const TemplateMedia: React.FC<{ template: QuestionnaireTemplateDTO }> = ({ template }) => {
  const { themeName } = useTheme();
  return (
    <Flex
      hAlign="center"
      vAlign="center"
      className={`template-card-list__item-avatar template-card-list__item-avatar--theme-${themeName}`}
    >
      <TemplateIconComponent template={template} size="small" />
    </Flex>
  );
};
